/**
 * staticDataService.ts
 *
 * The "Ideological Database" – the caching layer that sits between the
 * frontend and the actual Render backend.
 *
 * Architecture: Stale-While-Revalidate (SWR) + Jamstack Pre-rendering
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * Load Order (Priority Queue):
 *   [1] INSTANT  → Static JSON baked into Vercel CDN at build-time (0ms wait)
 *   [2] FAST     → Browser in-memory cache (already in this session)
 *   [3] WARM     → localStorage – persisted from a previous visit
 *   [4] LIVE     → Live backend API (after the visitor sees content)
 *
 * Data Structures:
 *   - Map<id, T>    : O(1) lookup by ID (hash map / dictionary)
 *   - T[]           : Ordered array for list rendering
 *   - Record<cat, T[]>: Grouped map for O(1) category filtering
 *
 * Programming Paradigms:
 *   - Singleton pattern     : One global instance shared across the app
 *   - Observer pattern      : CustomEvents notify consumers when live data arrives
 *   - Promise deduplication : One in-flight request per resource type
 *   - Fail-safe             : Every stage has a fallback; the UI never blocks
 *   - Immutability          : Static snapshot is never mutated; only replaced
 */

// ─── Types ─────────────────────────────────────────────────────────────────────
export interface StaticManifest {
  generatedAt: string;
  apiBase: string;
  status: { tours: boolean; categories: boolean; images: boolean };
  version: string;
}

// ─── Events emitted by this service ────────────────────────────────────────────
export const STATIC_DATA_EVENTS = {
  TOURS_READY:       'staticData:toursReady',
  CATEGORIES_READY:  'staticData:categoriesReady',
  IMAGES_READY:      'staticData:imagesReady',
  LIVE_TOURS_READY:  'staticData:liveTours',
  LIVE_CATS_READY:   'staticData:liveCategories',
  LIVE_IMGS_READY:   'staticData:liveImages',
} as const;

// ─── Service ────────────────────────────────────────────────────────────────────
class StaticDataService {
  // ── In-memory hash maps (O(1) lookups) ──────────────────────────────────────
  private toursMap:        Map<string, any> = new Map();
  private categoriesMap:   Map<string, any> = new Map();
  private imagesMap:       Map<string, any> = new Map();
  private imagesByCatMap:  Record<string, any[]> = {};

  // ── Ordered arrays for list rendering ───────────────────────────────────────
  private toursArray:      any[] = [];
  private categoriesArray: any[] = [];
  private imagesArray:     any[] = [];

  // ── State flags ─────────────────────────────────────────────────────────────
  private staticLoaded = false;
  private manifest: StaticManifest | null = null;

  // ── Pending deduplication promises ──────────────────────────────────────────
  private toursStaticPromise:      Promise<any[]> | null = null;
  private categoriesStaticPromise: Promise<any[]> | null = null;
  private imagesStaticPromise:     Promise<any[]> | null = null;

  // ─── Public bootstrap ─────────────────────────────────────────────────────
  /**
   * Call once at app startup (e.g. in index.tsx before ReactDOM.render).
   * Begins parallel static fetches immediately, so data is ready by the time
   * any component mounts.
   */
  async prefetch(): Promise<void> {
    const [tours, categories, images] = await Promise.allSettled([
      this.getStaticTours(),
      this.getStaticCategories(),
      this.getStaticImages(),
    ]);

    this.staticLoaded = true;

    if (tours.status       === 'fulfilled') this.dispatch(STATIC_DATA_EVENTS.TOURS_READY,      tours.value);
    if (categories.status  === 'fulfilled') this.dispatch(STATIC_DATA_EVENTS.CATEGORIES_READY, categories.value);
    if (images.status      === 'fulfilled') this.dispatch(STATIC_DATA_EVENTS.IMAGES_READY,     images.value);
  }

  // ─── Static JSON fetchers ──────────────────────────────────────────────────
  async getStaticTours(): Promise<any[]> {
    if (this.toursArray.length > 0) return [...this.toursArray];
    if (this.toursStaticPromise) return this.toursStaticPromise;

    this.toursStaticPromise = this.fetchStaticJson<any[]>('/data/tours.json')
      .then(data => {
        this.populateTours(data);
        this.toursStaticPromise = null;
        return [...this.toursArray];
      })
      .catch(() => {
        this.toursStaticPromise = null;
        return [];
      });

    return this.toursStaticPromise;
  }

  async getStaticCategories(): Promise<any[]> {
    if (this.categoriesArray.length > 0) return [...this.categoriesArray];
    if (this.categoriesStaticPromise) return this.categoriesStaticPromise;

    this.categoriesStaticPromise = this.fetchStaticJson<any[]>('/data/categories.json')
      .then(data => {
        this.populateCategories(data);
        this.categoriesStaticPromise = null;
        return [...this.categoriesArray];
      })
      .catch(() => {
        this.categoriesStaticPromise = null;
        return [];
      });

    return this.categoriesStaticPromise;
  }

  async getStaticImages(): Promise<any[]> {
    if (this.imagesArray.length > 0) return [...this.imagesArray];
    if (this.imagesStaticPromise) return this.imagesStaticPromise;

    this.imagesStaticPromise = this.fetchStaticJson<any[]>('/data/images.json')
      .then(data => {
        this.populateImages(data);
        this.imagesStaticPromise = null;
        return [...this.imagesArray];
      })
      .catch(() => {
        this.imagesStaticPromise = null;
        return [];
      });

    return this.imagesStaticPromise;
  }

  // ─── Synchronous getters (instant – already in memory) ────────────────────
  /** O(1) – returns from hash map */
  getTourById(id: string): any | null {
    return this.toursMap.get(id) ?? null;
  }

  /** O(1) – returns from hash map */
  getCategoryById(id: string): any | null {
    return this.categoriesMap.get(id) ?? null;
  }

  /** O(1) – returns from hash map */
  getImageById(id: string): any | null {
    return this.imagesMap.get(id) ?? null;
  }

  /** O(k) where k = tours in that category – from pre-grouped map */
  getImagesByCategory(category: string): any[] {
    return [...(this.imagesByCatMap[category] || [])];
  }

  /** O(n) – filtered from memory array */
  getToursByCategoryId(categoryId: string): any[] {
    return this.toursArray.filter(t => t.categoryId === categoryId || t.category_id === categoryId);
  }

  // Snapshot accessors
  getAllTours():      any[] { return [...this.toursArray]; }
  getAllCategories(): any[] { return [...this.categoriesArray]; }
  getAllImages():     any[] { return [...this.imagesArray]; }
  getManifest():     StaticManifest | null { return this.manifest; }
  isLoaded():        boolean { return this.staticLoaded; }

  // ─── Called by live services after a fresh backend fetch ──────────────────
  /**
   * Hydrate the in-memory cache with fresh live data.
   * Uses SWR: the static data was shown first, now we update silently.
   */
  hydrateLiveTours(tours: any[]): void {
    if (!tours || tours.length === 0) return;
    this.populateTours(tours);
    this.dispatch(STATIC_DATA_EVENTS.LIVE_TOURS_READY, tours);
  }

  hydrateLiveCategories(categories: any[]): void {
    if (!categories || categories.length === 0) return;
    this.populateCategories(categories);
    this.dispatch(STATIC_DATA_EVENTS.LIVE_CATS_READY, categories);
  }

  hydrateLiveImages(images: any[]): void {
    if (!images || images.length === 0) return;
    this.populateImages(images);
    this.dispatch(STATIC_DATA_EVENTS.LIVE_IMGS_READY, images);
  }

  // ─── Internal population helpers ──────────────────────────────────────────
  private populateTours(tours: any[]): void {
    if (!Array.isArray(tours) || tours.length === 0) return;
    this.toursMap.clear();
    for (const tour of tours) {
      if (tour?.id) this.toursMap.set(tour.id, tour);
    }
    this.toursArray = tours;
  }

  private populateCategories(cats: any[]): void {
    if (!Array.isArray(cats) || cats.length === 0) return;
    this.categoriesMap.clear();
    for (const cat of cats) {
      if (cat?.id) this.categoriesMap.set(cat.id, cat);
    }
    this.categoriesArray = cats;
  }

  private populateImages(imgs: any[]): void {
    if (!Array.isArray(imgs) || imgs.length === 0) return;
    this.imagesMap.clear();
    this.imagesByCatMap = {};
    for (const img of imgs) {
      if (!img?.id) continue;
      this.imagesMap.set(img.id, img);
      const cat = img.category || 'uncategorized';
      if (!this.imagesByCatMap[cat]) this.imagesByCatMap[cat] = [];
      this.imagesByCatMap[cat].push(img);
    }
    this.imagesArray = imgs;
  }

  // ─── Fetch a static JSON file from the CDN/public folder ─────────────────
  private async fetchStaticJson<T>(path: string): Promise<T> {
    // Add cache-busting only in development to ensure fresh local builds
    const url = process.env.NODE_ENV === 'development'
      ? `${path}?t=${Date.now()}`
      : path;

    const res = await fetch(url, {
      // 'default' allows CDN caching in production; browser gets it from cache after first load
      cache: 'default',
    });

    if (!res.ok) {
      throw new Error(`Static file not found: ${path} (${res.status})`);
    }

    return res.json() as Promise<T>;
  }

  // ─── Observer pattern: dispatch custom events ─────────────────────────────
  private dispatch(eventName: string, data: any): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  }
}

// Singleton export
export const staticDataService = new StaticDataService();
export default staticDataService;
